// Components==============
import { useNavigate } from "@tanstack/react-router";
import useCreateUser from "actions/user/useCreateUser";
import { UserInfoContext, UserInfoType } from "context/UserInfoContext";
import useNavigationAudio from "hooks/useNavigationAudio";
import useUserInfoNavigation from "hooks/useUserInfoNavigation";
import { useContext, useState } from "react";
import data from "utilities/extraDataOptions";
import QuizLayout from "../Quiz/components/QuizLayout/QuizLayout";
import ExtraUserOptions from "./components/ExtraUserOptions/ExtraUserOptions";
// =========================

export default function ExtraUserInfo() {
  const [endQuizModal, setEndQuizModal] = useState(false);
  const [extraUserInfoQuestionIndex, setExtraUserInfoQuestionIndex] =
    useState(0);
  const [currentPosition, setCurrentPosition] = useState(
    data[0]?.startIndex || 0,
  );

  const questionAndOptions = data[extraUserInfoQuestionIndex];

  const navigate = useNavigate();
  const { userInfo, setUserInfo, page, setPage } = useContext(UserInfoContext);
  const audio = useNavigationAudio();
  const { mutateAsync: createUser } = useCreateUser();

  // We pass data as an argument instead of reading from context to avoid stale reads
  const handleComplete = async (data: UserInfoType) => {
    const user = await createUser({
      age: parseInt(data.age.join("")),
      postcode: data.postcode.join(""),
      gender: data.gender,
      education_level: data.educationLevel,
      employment_status: data.employmentStatus,
      housing_type: data.housingType,
      living_situation: data.livingSituation,
      station:
        // @ts-ignore
        import.meta.env.VITE_IS_OTHER_STATION == "true" ? "low" : "high",
    });

    setUserInfo((prev: UserInfoType) => ({ ...prev, id: user.id }));
    navigate({ to: "/quiz" });
  };

  const handleEnter = () => {
    const selectedOption = questionAndOptions.options[currentPosition];
    if (!selectedOption) return;

    // Build the updated user info object synchronously
    const updated: UserInfoType = {
      ...userInfo,
      [questionAndOptions.id]: selectedOption.name,
    };

    const nextIndex = extraUserInfoQuestionIndex + 1;

    // Commit state and advance
    setUserInfo(updated);

    if (nextIndex < data.length) {
      setExtraUserInfoQuestionIndex(nextIndex);
      setCurrentPosition(data[nextIndex]?.startIndex || 0);
      return;
    }

    handleComplete(updated);
  };

  const handleNavigateBack = () => {
    if (extraUserInfoQuestionIndex > 0) {
      const prevIndex = extraUserInfoQuestionIndex - 1;
      setExtraUserInfoQuestionIndex(prevIndex);
      setCurrentPosition(data[prevIndex]?.startIndex || 0);
      return;
    }

    // Else, navigate back to Age page
    setPage((prevPage) => prevPage - 1);
    navigate({ to: "/age" });
  };

  const { backSelected, endQuizSelected, eventCounter } = useUserInfoNavigation(
    {
      audio,
      currentPosition,
      setCurrentPosition,
      onEnter: handleEnter,
      onNavigateBack: handleNavigateBack,
      setEndQuizModal,
      canNavigateBack: true,
      totalAmount: questionAndOptions.options.length,
    },
  );

  return (
    <QuizLayout
      title={questionAndOptions.question}
      titleAbsolute
      backSelected={backSelected}
      endQuizSelected={endQuizSelected}
      eventCounter={eventCounter}
      questionIndex={page}
      endQuizModal={endQuizModal}
      setEndQuizModal={setEndQuizModal}
    >
      <ExtraUserOptions
        options={questionAndOptions.options}
        currentPosition={currentPosition}
      />
    </QuizLayout>
  );
}
